import { forwardRef, useState, type ComponentPropsWithoutRef } from 'react'
import { Check, Eye, EyeOff, Lock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { evaluatePassword, passwordStrength, type PasswordPerson } from '@/lib/password-policy'
import { cn } from '@/utils/cn'

type PasswordInputProps = ComponentPropsWithoutRef<typeof Input> & {
  /** Lock icon on the left. */
  withIcon?: boolean
  /** Green tick inside the field (e.g. confirmation matches). */
  valid?: boolean
}

const ICON_BUTTON =
  'flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

/** Password input: optional lock icon, show/hide toggle, extra actions. */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { className, withIcon = false, valid = false, ...props },
  ref,
) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="relative">
      {withIcon ? (
        <Lock
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
          aria-hidden
        />
      ) : null}
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={cn(withIcon && 'pl-10', valid ? 'pr-16' : 'pr-11', className)}
        {...props}
      />
      <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
        {valid ? <Check className="mr-0.5 h-4 w-4 text-emerald-500" aria-label="Matches" /> : null}
        <button
          type="button"
          tabIndex={-1}
          className={ICON_BUTTON}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      </div>
    </div>
  )
})

const STRENGTH_COLORS = ['', 'bg-red-500', 'bg-amber-500', 'bg-sky-500', 'bg-emerald-500'] as const
const STRENGTH_TEXT = [
  'text-muted-foreground',
  'text-red-600 dark:text-red-400',
  'text-amber-600 dark:text-amber-400',
  'text-sky-600 dark:text-sky-400',
  'text-emerald-600 dark:text-emerald-400',
] as const

/** Strength bar + an aligned 3×2 checklist; each rule ticks as it is met. */
export function PasswordRequirements({
  value,
  person,
  className,
}: {
  value: string
  person?: PasswordPerson
  className?: string
}) {
  const rules = evaluatePassword(value, person)
  const { score, label } = passwordStrength(value, person)

  return (
    <div className={cn('space-y-2.5', className)}>
      <div className="flex items-center gap-3">
        <div className="grid flex-1 grid-cols-4 gap-1" aria-hidden>
          {[1, 2, 3, 4].map((step) => (
            <span
              key={step}
              className={cn(
                'h-1.5 rounded-full transition-colors duration-300',
                score >= step ? STRENGTH_COLORS[score] : 'bg-muted',
              )}
            />
          ))}
        </div>
        <span className={cn('w-14 text-right text-xs font-medium', STRENGTH_TEXT[score])} aria-live="polite">
          {label || 'Strength'}
        </span>
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3" aria-label="Password requirements">
        {rules.map((rule) => (
          <li
            key={rule.id}
            title={rule.label}
            className={cn(
              'flex min-w-0 items-center gap-2 text-xs transition-colors duration-200',
              rule.met ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <span
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-200',
                rule.met ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-muted-foreground/40',
              )}
              aria-hidden
            >
              {rule.met ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
            </span>
            <span className="truncate">{rule.short}</span>
            <span className="sr-only">{rule.met ? '(met)' : '(not met)'}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
